import React from 'react';
import { FormConnection } from '../Forms/FormConnection';
import { IconPhone } from '../Icons/IconPhone';
import { IconEmail } from '../Icons/IconEmail';
import { IconInstagram } from '../Icons/IconInstagram';
import IconBack from '../Icons/IconBack';
import { COLORS } from '../../../styles/UISystem';

/**
 * HeaderConnection - A fixed height header component for connection pages
 * 
 * @param {Object} props - Component props
 * @param {string|null} props.name - Name to display
 * @param {function} props.onNameChange - Function called when name is edited (for editable mode)
 * @param {boolean} props.isEditable - Whether the name field should be editable
 * @param {boolean} props.showBackButton - Whether to show the back button
 * @param {function} props.onBackClick - Function called when back button is clicked
 * @param {Object} props.contactInfo - Contact info object containing phone, email, and instagram fields
 * @param {boolean} props.isTestMode - If true, adds a very light grey background for testing layout
 * @returns {JSX.Element}
 */
const HeaderConnection = ({
  name = '',
  onNameChange,
  isEditable = false,
  showBackButton = true,
  onBackClick,
  contactInfo = {}
}) => {
  // Extract contact information
  const { phone, email, instagram } = contactInfo || {};
  
  return (
    <div 
      style={{
        position: 'relative',
        width: '100%',
        height: '110px',
        backgroundColor: 'white',
        padding: '16px',
        boxSizing: 'border-box'
      }}
    >
      {/* Name row with optional back button - positioned from bottom */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        height: '32px',
        bottom: '60px', /* Positioned 60px from bottom (12px + 40px + 8px) */
        left: '16px',
        right: '16px'
      }}>
        {/* Back button - aligned with name */}
        {showBackButton && (
          <div 
            onClick={onBackClick}
            style={{
              cursor: 'pointer',
              marginRight: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              position: 'absolute',
              left: '16px', /* Account for parent padding */
              top: '50%', /* Center vertically with name */
              transform: 'translateY(-50%)' /* Perfect vertical centering */
            }}
          >
            <IconBack />
          </div>
        )}
        
        {/* Name container - centered regardless of back button */}
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <div style={{ maxWidth: '80%', display: 'flex', justifyContent: 'center' }}>
          {isEditable ? (
            <FormConnection
              placeholder="Name"
              value={name || ''}
              onChange={onNameChange}
              style={{
                fontSize: name && name.length > 45 ? '16px' : (name && name.length > 35 ? '18px' : (name && name.length > 25 ? '20px' : '24px')),
                fontWeight: 'normal',
                height: '32px',
                margin: '0 auto',
                textAlign: 'center',
                width: '100%',
                backgroundColor: '#F0F0F2',
                borderRadius: '8px',
                padding: '0 16px',
                color: COLORS.DARK_GRAY
              }}
            />
          ) : (
            <div
              style={{
                fontSize: name && name.length > 45 ? '16px' : (name && name.length > 35 ? '18px' : (name && name.length > 25 ? '20px' : '24px')),
                fontWeight: 'normal',
                margin: '0 auto',
                padding: 0,
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                color: COLORS.DARK_GRAY,
                whiteSpace: 'nowrap' /* Prevent line breaks */
              }}
            >
              {name || ''}
            </div>
          )}
          </div>
        </div>
      </div>
      
      {/* Bottom row with contact icons - exactly 12px from bottom */}
      <div style={{
        position: 'absolute', 
        bottom: '12px', /* 12px from bottom */
        left: '16px',
        right: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px'
      }}>
        {phone && (
          <a href={`sms:${phone.replace(/[^0-9+]/g, '')}`} style={{ textDecoration: 'none' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#F0F0F2',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <IconPhone state="active" size={28} />
            </div>
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} style={{ textDecoration: 'none' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#F0F0F2',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <IconEmail state="active" size={28} />
            </div>
          </a>
        )}
        {instagram && (
          <a href={`https://instagram.com/${instagram.startsWith('@') ? instagram.substring(1) : instagram}`} style={{ textDecoration: 'none' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#F0F0F2',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <IconInstagram state="active" size={28} />
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

export default HeaderConnection;
